export type ContentCounts = {
  clips: number;
  quotes: number;
  articles: number;
  social: number;
  /**
   * How many of `clips` have a rendered file (`getVisibleClips`) — the same
   * signal the clip rows' aspect badges colour themselves by, so the rollup and
   * the badges beneath it can never disagree. Omitted by callers with no clip
   * graph to measure (folder rows), which then get no publish sub-line.
   */
  publishedClips?: number;
};

export type ContentItemKey = 'clips' | 'quotes' | 'articles' | 'social';

export type ContentItem = {
  key: ContentItemKey;
  count: number;
  label: string;
  /**
   * Prose sub-line beneath the label — rendered hanging under the label text.
   * Only clips carry one (how many of them are published).
   */
  detail?: string;
};

/**
 * How much of a story's clip output is published, as the sub-line under its
 * clip count — "1 published" beneath "8 Clips".
 *
 * Only the published half is named: the total sits directly above it, so the
 * draft count is arithmetic, and one number in a dense cell beats two. The
 * all-or-nothing cases take a word rather than a number, which would just echo
 * the total ("8 Clips / 8 published"). Sentence case — prose under a label, not
 * a second label.
 */
export const getClipPublishDetail = (
  clips: number,
  published: number,
): string | undefined => {
  if (clips === 0) return undefined;
  if (published <= 0) return 'None published';
  if (published >= clips) return 'All published';
  return `${published} published`;
};

/**
 * The Story Manager "Content" items: one per generated content type with a
 * non-zero count, in clips → quotes → articles → social order, each pluralised
 * (e.g. "10 Clips", "1 Article"). Zero-count types are omitted; an empty result
 * means nothing has been generated yet. Rendered by `ContentCell` as an
 * icon + label grid.
 */
export const getContentItems = (counts: ContentCounts): ContentItem[] => {
  const defs: Array<[ContentItemKey, number, string, string]> = [
    ['clips', counts.clips, 'Clip', 'Clips'],
    ['quotes', counts.quotes, 'Quote', 'Quotes'],
    ['articles', counts.articles, 'Article', 'Articles'],
    ['social', counts.social, 'Social', 'Social'],
  ];

  return defs
    .filter(([, count]) => count > 0)
    .map(([key, count, singular, plural]) => ({
      key,
      count,
      label: `${count} ${count === 1 ? singular : plural}`,
      ...(key === 'clips' &&
        counts.publishedClips !== undefined && {
          detail: getClipPublishDetail(counts.clips, counts.publishedClips),
        }),
    }));
};
